/**
 * Device Manager Store
 *
 * Централизованное управление состоянием камеры, микрофона и разрешений.
 * Единый источник правды для устройств, разрешений и preview-стрима.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getMediaDeviceId, setMediaDeviceId, getPreferredMicrophone, isSpeakerSelectionSupported } from '../utils/mediaDevicePrefs'
import { removeItem } from '../utils/storage'
import { VideoUtils } from '../components/conf/videoUtils'
import {
  classifyMediaError,
  DeviceConstraintError,
  PermissionDeniedError,
  DeviceUnavailableError
} from '../utils/errors'
import { createMediaRecovery } from './media/MediaRecovery'

export type PermissionState = 'prompt' | 'granted' | 'denied' | 'unavailable'

export interface DeviceInfo {
  id: string
  name: string
  extra?: string | null
  extraVariant?: 'default' | 'muted' | 'danger'
}

export const useDeviceManager = defineStore('deviceManager', () => {
  // U26 monitoring: count dead track recoveries after mobile background
  const deadTrackRecoveryCount = ref(0)

  const cameraPermission = ref<PermissionState>('prompt')
  const microphonePermission = ref<PermissionState>('prompt')

  const videoDevices = ref<DeviceInfo[]>([])
  const audioDevices = ref<DeviceInfo[]>([])
  const speakerDevices = ref<DeviceInfo[]>([])

  const selectedCamera = ref('')
  const selectedMicrophone = ref('')
  const selectedSpeaker = ref('')

  const previewStream = ref<MediaStream | null>(null)

  // i18n-ключ последней ошибки переключения камеры или null. Компонент с тостом
  // (VideoLayout) подписан на сигнал: Vue-контекстная работа (тост, useT) живёт
  // в компоненте, захват устройств — в сторе.
  const cameraSwitchError = ref<string | null>(null)

  // In-flight замок переключения камеры — защита от параллельных selectCamera.
  // Не reactive: чисто control-flow, UI на него не подписан.
  let isSwitchingCamera = false

  // requestAccess/loadDevices are hoisted function declarations, safe to reference here.
  const recovery = createMediaRecovery({
    previewStream,
    cameraPermission,
    microphonePermission,
    deadTrackRecoveryCount,
    requestAccess,
    loadDevices
  })

  const isCheckingPermissions = ref(false)
  const isLoadingDevices = ref(false)
  const isRequestingPermission = ref(false)

  // Permission check completed (true after initial checkPermissions resolves)
  const permissionsKnown = ref(false)

  // true when checkPermissions used the real Permissions API (not enumerate/localStorage fallback)
  const permissionsCheckedViaAPI = ref(false)

  const joinedWithoutMedia = ref(false)

  const isCameraGranted = computed(() => cameraPermission.value === 'granted')
  const isMicrophoneGranted = computed(() => microphonePermission.value === 'granted')
  const isCameraDenied = computed(() => cameraPermission.value === 'denied')
  const isMicrophoneDenied = computed(() => microphonePermission.value === 'denied')

  const hasPermission = computed(() =>
    cameraPermission.value === 'granted' && microphonePermission.value === 'granted'
  )

  const isPermissionDenied = computed(() =>
    cameraPermission.value === 'denied' || microphonePermission.value === 'denied'
  )

  const hasPromptPermission = computed(() =>
    cameraPermission.value === 'prompt' || microphonePermission.value === 'prompt'
  )

  const hasDeniedPermission = computed(() =>
    isCameraDenied.value || isMicrophoneDenied.value
  )

  const isPermissionConfirmedByAPI = computed(() =>
    permissionsCheckedViaAPI.value && (isCameraGranted.value || isMicrophoneGranted.value)
  )

  const hasVideoStream = computed(() => {
    if (!previewStream.value) return false
    const tracks = previewStream.value.getVideoTracks()
    return tracks.length > 0 && tracks.some(t => t.readyState === 'live')
  })

  const hasAudioStream = computed(() => {
    if (!previewStream.value) return false
    const tracks = previewStream.value.getAudioTracks()
    return tracks.length > 0 && tracks.some(t => t.readyState === 'live')
  })

  const isSpeakerSupported = computed(() => isSpeakerSelectionSupported())

  async function checkPermissions(): Promise<void> {
    isCheckingPermissions.value = true

    try {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      const isFirefox = /firefox/i.test(navigator.userAgent)

      if (!navigator.permissions || isSafari || isFirefox) {
        // Safari: нет Permissions API
        // Firefox: Permissions API всегда возвращает 'prompt' даже при реальном granted
        permissionsCheckedViaAPI.value = false
        await checkPermissionsViaEnumerate()
        return
      }

      // Chrome и Chromium — доверяем Permissions API
      const [cameraResult, micResult] = await Promise.allSettled([
        queryPermission('camera'),
        queryPermission('microphone')
      ])

      const camState = cameraResult.status === 'fulfilled' ? cameraResult.value : 'prompt'
      const micState = micResult.status === 'fulfilled' ? micResult.value : 'prompt'

      permissionsCheckedViaAPI.value = true
      cameraPermission.value = camState
      microphonePermission.value = micState
    } finally {
      isCheckingPermissions.value = false
      permissionsKnown.value = true
    }
  }

  async function queryPermission(name: 'camera' | 'microphone'): Promise<PermissionState> {
    try {
      const result = await navigator.permissions.query({ name: name as PermissionName })

      result.onchange = () => {
        const newState = mapPermissionState(result.state)
        const wasGranted = name === 'camera'
          ? cameraPermission.value === 'granted'
          : microphonePermission.value === 'granted'

        if (name === 'camera') {
          cameraPermission.value = newState
        } else {
          microphonePermission.value = newState
        }

        // Не при joinedWithoutMedia — composable сам обработает через tryUpgradeStream
        if (newState === 'granted' && !wasGranted && !joinedWithoutMedia.value) {
          setTimeout(() => { requestAccess() }, 100)
        }

        // Если разрешение отозвали, но другое ещё granted —
        // пересоздать стрим (Chrome останавливает ВСЕ треки getUserMedia при отзыве любого разрешения)
        if (newState === 'denied' && wasGranted) {
          const otherGranted = name === 'camera'
            ? microphonePermission.value === 'granted'
            : cameraPermission.value === 'granted'
          if (otherGranted) {
            setTimeout(() => { requestAccess() }, 100)
          }
        }
      }

      return mapPermissionState(result.state)
    } catch {
      return 'prompt'
    }
  }

  function mapPermissionState(state: string): PermissionState {
    switch (state) {
      case 'granted': return 'granted'
      case 'denied': return 'denied'
      case 'prompt': return 'prompt'
      default: return 'prompt'
    }
  }

  async function checkPermissionsViaEnumerate(): Promise<void> {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        cameraPermission.value = 'unavailable'
        microphonePermission.value = 'unavailable'
        return
      }

      // Safari не показывает device.label до первого getUserMedia в сессии.
      // Если есть сохранённые устройства — доверяем, что разрешения были даны ранее.
      const savedVideoId = getMediaDeviceId('video')
      const savedAudioId = getMediaDeviceId('audio')

      if (savedVideoId && savedAudioId) {
        cameraPermission.value = 'granted'
        microphonePermission.value = 'granted'
        return
      }

      const devices = await navigator.mediaDevices.enumerateDevices()

      const videoInputs = devices.filter(d => d.kind === 'videoinput')
      const audioInputs = devices.filter(d => d.kind === 'audioinput')

      if (videoInputs.length === 0) {
        cameraPermission.value = 'unavailable'
      } else if (savedVideoId) {
        cameraPermission.value = 'granted'
      } else {
        const hasLabel = videoInputs.some(d => d.label && d.label.length > 0)
        cameraPermission.value = hasLabel ? 'granted' : 'prompt'
      }

      if (audioInputs.length === 0) {
        microphonePermission.value = 'unavailable'
      } else if (savedAudioId) {
        microphonePermission.value = 'granted'
      } else {
        const hasLabel = audioInputs.some(d => d.label && d.label.length > 0)
        microphonePermission.value = hasLabel ? 'granted' : 'prompt'
      }
    } catch {
      cameraPermission.value = 'prompt'
      microphonePermission.value = 'prompt'
    }
  }

  /**
   * Clear a stale deviceId from localStorage when the device no longer exists.
   * Prevents repeated OverconstrainedError on subsequent getUserMedia calls.
   */
  function clearStaleDeviceId(kind: string, savedId: string | undefined): void {
    if (savedId) {
      console.warn(`[mediaDevices] Clearing stale ${kind} deviceId: ${savedId.slice(0, 8)}...`)
      removeItem(kind)
    }
  }

  // Called when combined getUserMedia throws OverconstrainedError — we don't know which device failed, so clear both.
  function clearStaleDeviceIds(videoId: string | undefined, audioId: string | undefined): void {
    clearStaleDeviceId('video', videoId)
    clearStaleDeviceId('audio', audioId)
  }

  async function requestAccess(): Promise<boolean> {
    // Don't create a new stream if the current one already has live tracks
    if (previewStream.value) {
      const hasLive = previewStream.value.getTracks().some(t => t.readyState === 'live')
      if (hasLive) return true
    }
    isRequestingPermission.value = true

    let stream: MediaStream | null = null
    let gotVideo = false
    let gotAudio = false

    let savedVideoId: string | undefined = getMediaDeviceId('video')
    let savedAudioId: string | undefined = getMediaDeviceId('audio')

    // Если нет сохранённого микрофона — выбираем лучший, чтобы избежать iPhone Continuity и других проблемных устройств
    if (!savedAudioId) {
      const preferred = await getPreferredMicrophone()
      if (preferred) {
        savedAudioId = preferred
        setMediaDeviceId('audio', preferred)
      }
    }

    try {
      stream = await VideoUtils.attachLocalVideo(savedVideoId, savedAudioId)
      gotVideo = true
      gotAudio = true
    } catch (error) {
      if (classifyMediaError(error) instanceof DeviceConstraintError) {
        clearStaleDeviceIds(savedVideoId, savedAudioId)
        savedVideoId = undefined
        savedAudioId = undefined
        try {
          stream = await VideoUtils.attachLocalVideo(undefined, undefined)
          gotVideo = true
          gotAudio = true
        } catch {
          // Default also failed, try separately
        }
      }
    }

    if (!gotVideo) {
      try {
        const videoStream = await VideoUtils.attachLocalVideo(savedVideoId, undefined, { videoOnly: true })
        if (stream) {
          videoStream.getVideoTracks().forEach(track => stream!.addTrack(track))
        } else {
          stream = videoStream
        }
        gotVideo = true
      } catch (error) {
        const classified = classifyMediaError(error, 'video')
        if (classified instanceof DeviceConstraintError) {
          clearStaleDeviceId('video', savedVideoId)
          savedVideoId = undefined
          try {
            const videoStream = await VideoUtils.attachLocalVideo(undefined, undefined, { videoOnly: true })
            if (stream) {
              videoStream.getVideoTracks().forEach(track => stream!.addTrack(track))
            } else {
              stream = videoStream
            }
            gotVideo = true
          } catch { /* handled below via permission checks */ }
        }
        if (!gotVideo) {
          if (classified instanceof PermissionDeniedError) {
            cameraPermission.value = 'denied'
          } else if (classified instanceof DeviceUnavailableError) {
            cameraPermission.value = 'unavailable'
          }
        }
      }
    }

    if (!gotAudio) {
      try {
        const audioStream = await VideoUtils.attachLocalVideo(undefined, savedAudioId, { audioOnly: true })
        if (stream) {
          audioStream.getAudioTracks().forEach(track => stream!.addTrack(track))
        } else {
          stream = audioStream
        }
        gotAudio = true
      } catch (error) {
        const classified = classifyMediaError(error, 'audio')
        if (classified instanceof DeviceConstraintError) {
          clearStaleDeviceId('audio', savedAudioId)
          savedAudioId = undefined
          try {
            const audioStream = await VideoUtils.attachLocalVideo(undefined, undefined, { audioOnly: true })
            if (stream) {
              audioStream.getAudioTracks().forEach(track => stream!.addTrack(track))
            } else {
              stream = audioStream
            }
            gotAudio = true
          } catch { /* handled below via permission checks */ }
        }
        if (!gotAudio) {
          if (classified instanceof PermissionDeniedError) {
            microphonePermission.value = 'denied'
          } else if (classified instanceof DeviceUnavailableError) {
            microphonePermission.value = 'unavailable'
          }
        }
      }
    }

    if (gotVideo) {
      cameraPermission.value = 'granted'
    }
    if (gotAudio) {
      microphonePermission.value = 'granted'
    }

    if (stream) {
      if (previewStream.value && previewStream.value !== stream) {
        previewStream.value.getTracks().forEach(t => t.stop())
      }
      previewStream.value = stream
      recovery.attachTrackEndedListeners(stream)
      recovery.startHealthMonitor()
      await loadDevices()

      // Если не было сохранённых устройств — синхронизируем с реально используемыми (чтобы dropdown совпадал с preview)
      if (!savedVideoId || !savedAudioId) {
        syncSelectedFromStream(stream, !savedVideoId, !savedAudioId)
      }

      isRequestingPermission.value = false
      return true
    }

    isRequestingPermission.value = false
    return false
  }

  function syncSelectedFromStream(stream: MediaStream, syncVideo: boolean, syncAudio: boolean): void {
    if (syncVideo) {
      const videoTrack = stream.getVideoTracks()[0]
      if (videoTrack) {
        const settings = videoTrack.getSettings()
        if (settings.deviceId) {
          selectedCamera.value = settings.deviceId
          setMediaDeviceId('video', settings.deviceId)
        }
      }
    }

    if (syncAudio) {
      const audioTrack = stream.getAudioTracks()[0]
      if (audioTrack) {
        const settings = audioTrack.getSettings()
        if (settings.deviceId) {
          selectedMicrophone.value = settings.deviceId
          setMediaDeviceId('audio', settings.deviceId)
        }
      }
    }
  }

  async function loadDevices(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      console.warn('enumerateDevices is not supported')
      return
    }

    isLoadingDevices.value = true

    try {
      const devices = await navigator.mediaDevices.enumerateDevices()

      const videoInputs = devices.filter(d => d.kind === 'videoinput')
      const hasVideoLabels = videoInputs.some(d => d.label && d.label.length > 0)

      if (hasVideoLabels || cameraPermission.value === 'granted') {
        videoDevices.value = videoInputs.map((d) => ({
          id: d.deviceId,
          name: d.label?.trim() || d.deviceId.slice(0, 8)
        }))
      }

      const audioInputs = devices.filter(d => d.kind === 'audioinput')
      const hasAudioLabels = audioInputs.some(d => d.label && d.label.length > 0)

      if (hasAudioLabels || microphonePermission.value === 'granted') {
        audioDevices.value = audioInputs.map((d) => ({
          id: d.deviceId,
          name: d.label?.trim() || d.deviceId.slice(0, 8)
        }))
      }

      if (isSpeakerSelectionSupported()) {
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput')
        const hasSpeakerLabels = audioOutputs.some(d => d.label && d.label.length > 0)

        if (hasSpeakerLabels || microphonePermission.value === 'granted') {
          speakerDevices.value = audioOutputs.map((d) => ({
            id: d.deviceId,
            name: d.label?.trim() || d.deviceId.slice(0, 8)
          }))
        }
      }

      await syncSelectedDevices()
    } catch (error) {
      console.error('Failed to load devices:', error)
    } finally {
      isLoadingDevices.value = false
    }
  }

  async function syncSelectedDevices(): Promise<void> {
    const savedCamera = getMediaDeviceId('video')
    if (savedCamera && videoDevices.value.some(d => d.id === savedCamera)) {
      selectedCamera.value = savedCamera
    } else if (videoDevices.value.length > 0) {
      selectedCamera.value = pickDefaultDevice(videoDevices.value)
    }

    const savedMic = getMediaDeviceId('audio')
    if (savedMic && audioDevices.value.some(d => d.id === savedMic)) {
      selectedMicrophone.value = savedMic
    } else if (audioDevices.value.length > 0) {
      selectedMicrophone.value = pickDefaultDevice(audioDevices.value)
    }

    const savedSpeaker = getMediaDeviceId('speaker')
    if (savedSpeaker && speakerDevices.value.some(d => d.id === savedSpeaker)) {
      selectedSpeaker.value = savedSpeaker
    } else if (speakerDevices.value.length > 0) {
      selectedSpeaker.value = pickDefaultDevice(speakerDevices.value)
    }
  }

  function pickDefaultDevice(devices: DeviceInfo[]): string {
    const defaultDevice = devices.find(d => d.id === 'default')
    return defaultDevice?.id ?? devices[0]?.id ?? ''
  }

  async function selectCamera(deviceId: string): Promise<void> {
    if (!deviceId) return

    const stream = previewStream.value
    if (!stream) {
      // Нет живого стрима — только запоминаем выбор, реальный захват сделает requestAccess()
      selectedCamera.value = deviceId
      setMediaDeviceId('video', deviceId)
      return
    }

    // Замок от параллельных переключений (двойной тап по кнопке): иначе оба вызова
    // сняли бы снапшот одного oldStream, оба остановили бы его видео и запустили бы
    // два getUserMedia — трек «проигравшего» вызова остался бы жив (утечка камеры).
    if (isSwitchingCamera) return
    isSwitchingCamera = true

    // Глушим health-монитор на время переключения — в окне между stop() старого
    // видео и applyPreviewStream previewStream содержит зомби-трек, и recovery
    // стартовал бы параллельно захвату.
    recovery.beginSwitch()
    try {
      await switchCameraTrack(stream, deviceId, selectedCamera.value)
    } finally {
      recovery.endSwitch()
      isSwitchingCamera = false
    }
  }

  /**
   * Переключение камеры на мобильных: сначала останавливаем старый ВИДЕО-трек,
   * только потом открываем новую камеру. На большинстве Android Chrome не может
   * открыть вторую камеру, пока первая занята (NotReadableError). Аудио-трек
   * переносим из старого стрима в новый — микрофон не пересоздаём (нет щелчка,
   * меньше поверхность отказа). selectedCamera/localStorage пишем только после
   * успешного захвата, при провале откатываем — меню всегда отражает реальность.
   */
  async function switchCameraTrack(oldStream: MediaStream, deviceId: string, prevCameraId: string): Promise<void> {
    // Переносим аудио-трек даже если он не live (мёртвый после звонка/будильника):
    // тогда монитор здоровья увидит зомби и сам поднимет полный recovery микрофона.
    // Иначе стрим собрался бы без аудио, монитор счёл бы его здоровым — микрофон
    // молчал бы до перезахода.
    const audioTrack = oldStream.getAudioTracks()[0] ?? null

    oldStream.getVideoTracks().forEach(t => t.stop())

    let videoTrack: MediaStreamTrack
    try {
      videoTrack = await captureCameraVideo(deviceId)
    } catch (error) {
      await rollbackCamera(prevCameraId, audioTrack, error)
      return
    }

    applyPreviewStream(audioTrack, videoTrack)

    // Fallback по facingMode мог открыть ДРУГУЮ камеру — сохраняем фактический id,
    // иначе следующий requestAccess снова упрётся в неоткрываемый deviceId.
    const actualId = videoTrack.getSettings().deviceId || deviceId
    selectedCamera.value = actualId
    setMediaDeviceId('video', actualId)
  }

  /**
   * Захват видео-трека новой камеры. Fallback для фронталки: на части Android
   * камера по deviceId-only недоступна (NotReadableError / чёрный кадр) —
   * повторяем через facingMode:{exact:'user'}.
   */
  async function captureCameraVideo(deviceId: string): Promise<MediaStreamTrack> {
    try {
      const stream = await VideoUtils.attachLocalVideo(deviceId, undefined, { videoOnly: true })
      return stream.getVideoTracks()[0]
    } catch (error) {
      const classified = classifyMediaError(error, 'video')
      if (classified instanceof DeviceUnavailableError && isFrontCamera(deviceId)) {
        const stream = await VideoUtils.attachLocalVideo(undefined, undefined, { videoOnly: true, videoFacingMode: 'user' })
        return stream.getVideoTracks()[0]
      }
      throw error
    }
  }

  async function rollbackCamera(prevCameraId: string, audioTrack: MediaStreamTrack | null, error: unknown): Promise<void> {
    console.error('[deviceManager] Failed to switch camera, rolling back:', error)

    // Возвращаем предыдущую камеру, чтобы self-view не остался без видео
    try {
      const stream = await VideoUtils.attachLocalVideo(prevCameraId || undefined, undefined, { videoOnly: true })
      applyPreviewStream(audioTrack, stream.getVideoTracks()[0])
    } catch (rollbackError) {
      console.error('[deviceManager] Camera rollback capture failed:', rollbackError)
      // Откатный захват тоже упал — публикуем честный стрим без видео (аудио-only),
      // чтобы реф сменился: watch в useDeviceControls снимет camInteractable, кнопка
      // камеры не будет притворяться рабочей, монитор увидит реальную картину.
      applyPreviewStream(audioTrack, undefined)
    }

    selectedCamera.value = prevCameraId
    setMediaDeviceId('video', prevCameraId)
    cameraSwitchError.value = cameraSwitchErrorKey(error)
  }

  function applyPreviewStream(audioTrack: MediaStreamTrack | null, videoTrack: MediaStreamTrack | undefined): void {
    const stream = new MediaStream()
    if (audioTrack) stream.addTrack(audioTrack)
    if (videoTrack) stream.addTrack(videoTrack)

    // Останавливаем треки текущего previewStream, которые не перешли в новый
    // (например видео «проигравшего» вызова) — иначе они остались бы жить в
    // выброшенном стриме и держали бы камеру занятой (NotReadableError).
    const previous = previewStream.value
    if (previous && previous !== stream) {
      const kept = new Set(stream.getTracks())
      previous.getTracks().forEach(t => { if (!kept.has(t)) t.stop() })
    }

    previewStream.value = stream
    recovery.attachTrackEndedListeners(stream)
    recovery.startHealthMonitor()
  }

  function isFrontCamera(deviceId: string): boolean {
    const label = videoDevices.value.find(d => d.id === deviceId)?.name.toLowerCase() ?? ''
    return label.includes('front') || label.includes('user') || label.includes('фронт')
  }

  function cameraSwitchErrorKey(error: unknown): string {
    const classified = classifyMediaError(error, 'video')
    if (classified instanceof PermissionDeniedError) return 'errors.mediaAccessDenied'
    if (classified instanceof DeviceUnavailableError) {
      return classified.reason === 'busy' ? 'errors.deviceBusy' : 'errors.mediaNotFound'
    }
    if (classified instanceof DeviceConstraintError) return 'errors.mediaOverconstrained'
    return 'errors.cameraSwitchFailed'
  }

  async function selectMicrophone(deviceId: string): Promise<void> {
    if (!deviceId) return

    selectedMicrophone.value = deviceId
    setMediaDeviceId('audio', deviceId)

    if (previewStream.value) {
      try {
        const videoId = selectedCamera.value || undefined
        const newStream = await VideoUtils.attachLocalVideo(videoId, deviceId)

        previewStream.value.getTracks().forEach(t => t.stop())
        previewStream.value = newStream
        recovery.attachTrackEndedListeners(newStream)
        recovery.startHealthMonitor()
      } catch (error) {
        console.error('Failed to switch microphone:', error)
      }
    }
  }

  function selectSpeaker(deviceId: string): void {
    if (!deviceId) return
    selectedSpeaker.value = deviceId
    setMediaDeviceId('speaker', deviceId)
  }

  function getBrowserInstructionsKey(): string {
    const ua = navigator.userAgent.toLowerCase()
    if (ua.includes('chrome')) return 'permissions.instructionsChrome'
    if (ua.includes('firefox')) return 'permissions.instructionsFirefox'
    if (ua.includes('safari')) return 'permissions.instructionsSafari'
    return 'permissions.instructionsDefault'
  }

  function cleanup(): void {
    recovery.stop()
    if (previewStream.value) {
      previewStream.value.getTracks().forEach(t => t.stop())
      previewStream.value = null
    }
  }

  const onDeviceChange = () => { loadDevices() }

  function setupDeviceChangeListener(): void {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', onDeviceChange)
    }
    document.addEventListener('visibilitychange', recovery.onVisibilityChange)
    window.addEventListener('pagehide', recovery.onPageHide)
  }

  function removeDeviceChangeListener(): void {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange)
    }
    document.removeEventListener('visibilitychange', recovery.onVisibilityChange)
    window.removeEventListener('pagehide', recovery.onPageHide)
  }

  return {
    cameraPermission,
    microphonePermission,
    videoDevices,
    audioDevices,
    speakerDevices,
    selectedCamera,
    selectedMicrophone,
    selectedSpeaker,
    previewStream,
    cameraSwitchError,
    isCheckingPermissions,
    isLoadingDevices,
    isRequestingPermission,
    permissionsKnown,
    permissionsCheckedViaAPI,
    isPermissionConfirmedByAPI,
    joinedWithoutMedia,
    isCameraGranted,
    isMicrophoneGranted,
    isCameraDenied,
    isMicrophoneDenied,
    hasPermission,
    isPermissionDenied,
    hasPromptPermission,
    hasDeniedPermission,
    hasVideoStream,
    hasAudioStream,
    isSpeakerSupported,
    checkPermissions,
    requestAccess,
    loadDevices,
    syncSelectedDevices,
    selectCamera,
    selectMicrophone,
    selectSpeaker,
    getBrowserInstructionsKey,
    cleanup,
    setupDeviceChangeListener,
    removeDeviceChangeListener,
    deadTrackRecoveryCount
  }
})
